"use client";
import React, { useState } from "react";
import Image from "next/image";
import SearchBar from "@/components/shared/searchBar";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/shadcn/button";

const SupportCenterPage: React.FC = () => {
  const router = useRouter();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const faqs = [
    {
      id: 1,
      question: "How do I hire a freelancer or agency on ProZ.com?",
      answer: `To find a language professional or company on ProZ.com, follow these steps:
    - <a target="_blank" href="https://www.proz.com/find" class="text-primary">Use the ProZ.com directories</a> – You can search for freelancers and companies based on language combinations, services offered, and other criteria. Visit the freelancers directory or the business directory to start your search, and contact providers directly with your offer.
    - <a target="_blank" href="https://www.proz.com/post-translation-job" class="text-primary">Post a job offer</a> – If you're looking for specific skills or a project-based collaboration, you can post a public offer. Matching candidates will apply, and you can review their profiles, rates, and qualifications before following up.
    - Let the team handle recruitment for you – <a target="_blank" href="https://www.proz.com/managed-services" class="text-primary">Contact the Managed Services team</a>, share your needs, and let us take care of the rest.`,
    },
    {
      id: 2,
      question: "How can I find translation work on ProZ.com?",
      answer: `There are two main ways to connect with clients on ProZ.com:
    - Clients can find you through the directory – Up to 80% of work on ProZ.com comes from clients who search the ProZ.com directory and contact professionals directly. <a target="_blank" href="https://www.proz.com/profile-completion" class="text-primary">Make sure your profile is complete</a> and includes your language pairs and services to appear in search results.
    - You can apply to public job postings – <a href="https://www.proz.com/language-jobs" target="_blank" class="text-primary">Browse posted job offers</a> —if you meet the poster’s criteria, you’ll see a green notice and a way to apply.
    For more tips, visit the <a href="https://www.proz.com/forum/getting_established-15" target="_blank" class="text-primary">Getting Established forum</a> or join one of ProZ.com’s free webinars for new users.`,
    },
    {
      id: 3,
      question: "How can I tell if a job offer is legitimate?",
      answer: `ProZ.com has security measures in place and conducts some manual vetting of outsourcers, but it’s still important for users to evaluate each offer carefully and use their best judgment.
    Here are some tips to help you assess the legitimacy of a job offer:
    - Do your research – If the person claims to represent a company, verify their identity and affiliation. Check for a matching email domain, website, and contact details.
    - Consult the <a href="http://proz.me/riskmanagement" target="_blank" class="text-primary">Risk Management Center</a> and the <a href="https://www.proz.com/blueboard/" target="_blank" class="text-primary">Blue Board</a> – Use the resources available at ProZ.com to check for red flags and read scam alerts, and see if others have reported issues with the outsourcer.
    - Trust your instincts – If something feels off —unusual urgency, vague details, suspicious email formatting, or requests to work outside standard channels— proceed with caution.
    ProZ.com encourages all professionals to apply personal discretion and sound risk management when evaluating job offers —just as you would when agreeing to unpaid tests, setting your rates, or accepting payment terms. If in doubt, feel free to <a href="https://www.proz.com/support?&mode=ask" target="_blank" class="text-primary">contact support</a> for guidance.`,
    },
    {
      id: 4,
      question: "Who can help me complete my profile?",
      answer: `If you're having trouble completing your profile, <a target="_blank" href="https://www.proz.com/support?&mode=ask" class="text-primary">the ProZ.com team can help</a>. Site staff can guide you through filling in missing information and offer suggestions to make your profile more attractive to potential clients.
    To get started, you can review this guide to improving your profile. Once you’ve made updates, feel free to <a href="https://www.proz.com/support?&mode=ask" target="_blank" class="text-primary">contact support</a> for personalized feedback to help you stand out.`,
    },
    {
      id: 5,
      question: "What is the new ProZ.com site and how do I go back to the old version?",
      answer: `What you are seeing is the beta version of ProZ.com's new site. During this phase, both the new site and the classic ProZ.com will coexist, as some sections of the beta site will not be fully functional yet. If you encounter any issues or have feedback, feel free to <a href="https://www.proz.com/support?&mode=ask" target="_blank" class="text-primary">contact support</a>.
    The new site will eventually replace the classic one, once it’s fully ready. In the meantime, to return to the classic site, simply head to <a href="http://www.proz.com" target="_blank" class="text-primary">proz.com</a>.`,
    },
    {
      id: 6,
      question: "How do I control who can message me?",
      answer: `To control who can message you, go to <a href="https://www.proz.com/?sp=profile&sp_mode=settings&sp_submode=ef" target="_blank" class="text-primary">Profile mail settings</a> in "My communication settings" (found in the top drop-down menu) or in the "Settings" tab of your profile under "Email preferences".
    You can choose from four options:
    - Allow anyone to message me – All users can send you messages, even without an account.
    - Allow logged-in ProZ.com users only – Only logged-in users can send you messages.
    - Allow ProZ.com members only – Only paying members can message you.
    - Allow no one to message me – No one can send you messages.`,
    },
    {
      id: 7,
      question: "I’m not receiving emails — what should I do?",
      answer: `If you're not receiving emails from ProZ.com or they are landing in your spam/junk folder, follow these steps to resolve the issue:
    - Check your <a href="https://www.proz.com/?sp=profile&sp_mode=settings&sp_submode=ef" target="_blank" class="text-primary">email preferences</a> – Ensure they are configured correctly on your ProZ.com account.
    - Add ProZ.com to your safe sender list – To ensure email delivery, add @proz.com to your safe sender list or whitelist.
    - If you use Gmail, Yahoo, Outlook, or other email services, make sure ProZ.com emails are not being filtered into tabs (like Gmail's Promotions or Updates tabs).
    - Mark ProZ.com emails as “Not Spam” – If emails are already in your spam folder, mark them as “Not Spam” or “Safe”.
    - Whitelist specific ProZ.com addresses – Add the following email addresses to your whitelist to ensure you receive important notifications:
      &nbsp; bounce-handler@proz.com
      &nbsp; notifications@proz.com
      &nbsp; support@proz.com
      &nbsp; profiles@proz.com
      &nbsp; transactions@proz.com
    If you're still not receiving emails, <a href="https://www.proz.com/support?&mode=ask" target="_blank" class="text-primary">contact support</a>.`,
    },
    {
      id: 8,
      question: "I can’t log in — password isn’t working or reset email not received",
      answer: `If you’re getting an “Incorrect password” message:
    - Double-check your password — it’s case-sensitive.
    - Turn off Caps Lock and check for typos or extra spaces.
    - Make sure your browser isn’t auto-filling an outdated password.
    Still stuck? <a href="https://www.proz.com/user/forgot-password" target="_blank" class="text-primary">Reset your password</a>.
    If you don’t receive the password reset email:
    - Check your spam or junk folder.
    - Search your inbox for emails from ProZ.com or with the subject “Password reset”.
    - Make sure you entered the same email used for your account.
    If needed, try resetting again or <a href="https://www.proz.com/support?&mode=ask" target="_blank" class="text-primary">contact support</a> and we’ll help you regain access.`,
    },
    {
      id: 9,
      question: "I keep getting logged out — what can I do?",
      answer: `If you're getting logged out immediately after logging in —especially when moving to a different page— it's likely due to issues with your browser's cookies or cache.
    Here’s what you can try:
    - <a target="_blank" href="http://www.proz.com/faq/login_problems#what_are_cookies" class="text-primary">Enable cookies</a>: Make sure your browser is set to allow cookies from our site. If cookies are blocked, the site won’t be able to remember your login session.
    - <a href="https://www.wikihow.com/Clear-Cache-and-Cookies" target="_blank" class="text-primary">Clear your browser cache and cookies</a>: Sometimes corrupted or outdated cookies can cause session problems. Clearing them can resolve the issue.
    - Disable privacy extensions or strict settings: Extensions or browser settings that block cookies or third-party scripts may interfere with login functionality.
    After adjusting your settings, try logging in again. If the problem continues, try a different browser or <a href="https://www.proz.com/support?&mode=ask" target="_blank" class="text-primary">contact support</a> for further assistance.`,
    },
    {
      id: 10,
      question: "I’m having issues with the site — how do I find out what’s wrong?",
      answer: `If something isn’t working as expected, try these quick troubleshooting steps to help isolate the issue:
    - Try a different browser – For example, switch from Opera to Chrome or Firefox. Some issues are browser-specific.
    - Use a different device – See if the problem persists on your phone, tablet, or another computer.
    - Test a different network – Switching to mobile data or another Wi-Fi connection can help rule out network-related issues.
    - Check your browser’s developer console – This can reveal error messages that may help identify the source of the problem (right-click on the page > click "Inspect" > click on the "Console" tab).
    If the issue continues after these steps, please <a href="https://www.proz.com/support?&mode=ask" target="_blank" class="text-primary">contact support</a> and let us know what you’ve tried —this will allow us to help you faster.`,
    },
  ];

  const handleClickFaq = (id: number) => {
    if (activeFaq === id) {
      setActiveFaq(null);
    } else {
      setActiveFaq(id);
    }
  };

  const handleSearch = (searchTerm: string) => {
    if (searchTerm) {
      window.open("https://www.proz.com/faq/?sp_mode=search&search_term=" + searchTerm, "_blank");
    }
  };

  const faqGroups = [
    {
      id: "getting-started",
      icon: "💼",
      title: "Getting Started & Finding Work",
      faqIds: [1, 2, 4],
    },
    {
      id: "safety",
      icon: "🛡️",
      title: "Safety & Legitimacy",
      faqIds: [3, 6],
    },
    {
      id: "navigation",
      icon: "🌐",
      title: "New Site & Navigation",
      faqIds: [5],
    },
    {
      id: "email-notifications",
      icon: "✉️",
      title: "Email & Notifications",
      faqIds: [7],
    },
    {
      id: "login-access",
      icon: "🔐",
      title: "Login & Access Issues",
      faqIds: [8, 9],
    },
    {
      id: "troubleshooting",
      icon: "🛠",
      title: "Technical Troubleshooting",
      faqIds: [10],
    },
  ];

  return (
    <>
      <div className="w-full px-3 pt-6 pb-5 bg-secondary inline-flex flex-col justify-start items-center gap-4 overflow-hidden dark:bg-primary-900 relative">
        <div
          className="absolute inset-0 bg-[url('/next/next_assets/images/linen_texture.png')] bg-cover bg-no-repeat opacity-10 pointer-events-none"
          style={{
            borderRadius: "inherit",
          }}
        ></div>
        <div className="relative z-10 self-stretch flex flex-col justify-start items-center gap-5">
          <div className="self-stretch flex flex-col justify-start items-center gap-2">
            <div className="size- flex flex-col justify-center items-center gap-6">
              <div className="size- flex flex-col justify-center items-center gap-1">
                <h1 className="text-primary text-5xl font-bold font-merriweather leading-[60px] tracking-tight mb-3 text-center lg:text-left">
                  ProZ help center
                </h1>
                <p className="text-center justify-start text-base font-light leading-relaxed">
                  Here you can find answers to common questions, explore guides, or contact our
                  support team.
                </p>
              </div>
              <SearchBar
                label={""}
                onInputChange={handleSearch}
                placeholder={"I need help with..."}
              />
            </div>
          </div>

          {/* Sub menu */}
          <div className="inline-flex justify-center items-center gap-1 overflow-x-auto flex-nowrap w-full">
            <div
              data-status="Hover"
              className="px-2 py-0.5 flex justify-center items-center gap-2.5 min-w-max"
            >
              <Link href={"#"}>
                <p className="text-center justify-start text-primary text-base font-semibold leading-loose">
                  Home
                </p>
              </Link>
            </div>
            <div className="text-center justify-start text-[#1d2939] text-base font-normal leading-7 min-w-max">
              |
            </div>
            <div
              data-status="Hover"
              className="px-2 py-0.5 flex justify-center items-center gap-2.5 min-w-max"
            >
              <Link href={"https://www.proz.com/support/my_requests"}>
                <p className="text-center justify-start text-grey-400 text-base font-normal leading-7 hover:text-primary hover:font-semibold">
                  Support requests
                </p>
              </Link>
            </div>
            <div className="text-center justify-start text-[#1d2939] text-base font-normal leading-7 min-w-max">
              |
            </div>
            <div
              data-status="Default"
              className="px-2 py-0.5 flex justify-center items-center gap-2.5 min-w-max"
            >
              <Link href={"https://www.proz.com/faq"}>
                <p className="text-center justify-start text-grey-400 text-base font-normal leading-7 hover:text-primary hover:font-semibold ">
                  FAQs & knowledgebase
                </p>
              </Link>
            </div>
            <div className="text-center justify-start text-[#1d2939] text-base font-normal leading-7 min-w-max">
              |
            </div>
            <div
              data-status="Default"
              className="px-2 py-0.5 flex justify-center items-center gap-2.5 min-w-max"
            >
              <Link
                href={
                  "https://www.proz.com/status?hsCtaTracking=91439bc9-0e05-4101-aa9d-1128de0cc9f1%7Cc71daca0-1117-475b-827e-74aa0deabc7c"
                }
              >
                <p className="text-center justify-start text-grey-400 text-base font-normal leading-7 hover:text-primary hover:font-semibold">
                  Site status
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-6 md:px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 my-8">
          <Link
            href="https://www.proz.com/faq/jobs.html#finding_jobs_at_proz_com"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center mb-4">
              <Image
                src="/next/next_assets/images/support/opportunities.svg"
                alt="support center Jobs"
                width={40}
                height={40}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              Jobs
            </div>
          </Link>
          <Link
            href="https://www.proz.com/faq/jobs.html#posting_jobs_at_proz_com"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center">
              <Image
                src="/next/next_assets/images/support/recruitment.svg"
                alt="support center Recruitment"
                width={36}
                height={36}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              Recruitment
            </div>
          </Link>
          <Link
            href="https://www.proz.com/faq/profiles.html"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center">
              <Image
                src="/next/next_assets/images/support/account.svg"
                alt="support center Account & profile"
                width={40}
                height={40}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              Account & profile
            </div>
          </Link>
          <Link
            href="https://www.proz.com/next/faq?category=membership"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center">
              <Image
                src="/next/next_assets/images/support/membership.svg"
                alt="support center Membership"
                width={40}
                height={40}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              Membership
            </div>
          </Link>
          <Link
            href="https://www.proz.com/faq/proz%2Apay.html"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center">
              <Image
                src="/next/next_assets/images/support/ppay.svg"
                alt="support center ProZ*Pay"
                width={56}
                height={56}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              ProZ*Pay
            </div>
          </Link>
          <Link
            href="https://www.proz.com/faq/proz_com_events.html"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center">
              <Image
                src="/next/next_assets/images/support/learning.svg"
                alt="support center Learning & events"
                width={35}
                height={35}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              Learning & events
            </div>
          </Link>
          <Link
            href="https://www.proz.com/faq/community.html"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center">
              <Image
                src="/next/next_assets/images/support/community.svg"
                alt="support center Community"
                width={35}
                height={35}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              Community
            </div>
          </Link>
          <Link
            href="https://www.proz.com/faq/billing_and_payment.html"
            target="_blank"
            className="flex flex-col items-center p-10 bg-white dark:bg-black rounded-3xl shadow hover:bg-accent hover:outline-primary outline outline-1 outline-grey-200 transition-all min-h-56 hover:scale-105"
          >
            <div className="flex-1 w-20 h-1/2 bg-secondary dark:bg-dark rounded-2xl flex items-center justify-center">
              <Image
                src="/next/next_assets/images/support/billing.svg"
                alt="support center Billing & payment"
                width={38}
                height={38}
                className="object-contain"
              />
            </div>
            <div className="flex-1 flex items-center justify-center w-full text-center text-grey-700 text-xl font-bold font-merriweather dark:text-gray-300 h-1/2">
              Billing & payment
            </div>
          </Link>
        </div>
      </div>

      <div className="bg-secondary">
        <div className="max-w-6xl mx-auto w-full flex-col py-14 justify-center items-center gap-8">
          <div className="flex flex-col justify-center items-center gap-4 pb-4">
            <p className="text-primary-600 dark:text-primary-50 text-2xl font-bold font-merriweather">
              ProZ support team
            </p>
            <p className="text-center text-base font-normal leading-relaxed">
              The team is here to help you from Monday to Friday, from 06:00 to 21:00 GMT, with
              limited coverage on weekends, and we are always working on expanding our availability.
              ProZ paying members receive priority support.
            </p>
          </div>

          {/* Team */}
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-black rounded-3xl flex flex-col items-center p-6 hover:scale-105 transition-all">
              <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-4">
                <Image
                  className="object-cover aspect-4/5"
                  src="/next/next_assets/images/staff/naiSupport.png"
                  alt="support member Naiara"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className="flex flex-col items-center gap-2">
                <p className="text-center text-grey-70 text-xl font-bold">Naiara Solano</p>
                <div className="text-center text-grey-600 dark:text-grey-200 text-sm">
                  <span>Support coordinator</span>
                  <br />
                  <span>Argentina</span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-black rounded-3xl flex flex-col items-center p-6 hover:scale-105 transition-all">
              <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-4">
                <Image
                  className="object-cover"
                  src="/next/next_assets/images/staff/andreSupport.png"
                  alt="support member Andrea"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className="w-full flex flex-col items-center">
                <p className="text-center text-grey-70 text-xl font-bold">Andrea Capuselli</p>
                <p className="text-center text-grey-600 dark:text-grey-200 text-sm">
                  <span>Support member</span>
                  <br />
                  <span>Argentina</span>
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-black rounded-3xl flex flex-col items-center p-6 hover:scale-105 transition-all">
              <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-4">
                <Image
                  className="object-cover"
                  src="/next/next_assets/images/staff/karenSupport.png"
                  alt="support member Karen"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className="w-full flex flex-col items-center">
                <p className="text-center text-grey-70 text-xl font-bold">Karen Zaragoza</p>
                <p className="text-center text-grey-600 dark:text-grey-200 text-sm">
                  <span>Support member</span>
                  <br />
                  <span>Philippines</span>
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-black rounded-3xl flex flex-col items-center p-6 hover:scale-105 transition-all">
              <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-4">
                <Image
                  className="object-cover"
                  src="/next/next_assets/images/staff/josephSupport.png"
                  alt="support member Joseph"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className="w-full flex flex-col items-center">
                <p className="text-center text-grey-70 text-xl font-bold">Joseph Oyange</p>
                <p className="text-center text-grey-600 dark:text-grey-200 text-sm">
                  <span>Support member</span>
                  <br />
                  <span>Kenya</span>
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-black rounded-3xl flex flex-col items-center p-6 hover:scale-105 transition-all">
              <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-4">
                <Image
                  className="object-cover"
                  src="/next/next_assets/images/staff/agostinaSupport.png"
                  alt="support member Agostina"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className="w-full flex flex-col items-center">
                <p className="text-center text-grey-70 text-xl font-bold">Agostina Menghini</p>
                <p className="text-center text-grey-600 dark:text-grey-200 text-sm">
                  <span>Support member</span>
                  <br />
                  <span>Argentina</span>
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-black rounded-3xl flex flex-col items-center p-6 hover:scale-105 transition-all">
              <div className="relative w-full h-56 rounded-2xl overflow-hidden mb-4">
                <Image
                  className="object-cover"
                  src="/next/next_assets/images/staff/anaSupport.png"
                  alt="support member Ana"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className="w-full flex flex-col items-center">
                <p className="text-center text-grey-70 text-xl font-bold">Ana Moirano</p>
                <p className="text-center text-grey-600 dark:text-grey-200 text-sm">
                  <span>Support member</span>
                  <br />
                  <span>Argentina</span>
                </p>
              </div>
            </div>
          </div>

          {/* Ask for help */}
          <div className="flex items-center justify-center w-full pt-8">
            <Link href={"https://www.proz.com/support?&mode=ask"} target="_blank">
              <Button variant="default" size="lg">
                <Image
                  src="/next/next_assets/images/svg/sc-plus-button.svg"
                  alt="support center Ask for help"
                  width={40}
                  height={40}
                  className="object-contain w-full h-auto rounded-3xl"
                />

                <p className="justify-start text-white text-xl font-bold font-['Poppins'] leading-loose">
                  Ask for help
                </p>
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="w-full py-12 container flex flex-col justify-start items-center gap-8">
        <div className="self-stretch text-center justify-start text-[#2e6969] text-3xl font-bold font-merriweather leading-9">
          Frequently asked questions
        </div>
        <div className="w-full pb-6 dark:bg-dark rounded-lg">
          {faqGroups.map((group) => (
            <div key={group.id} className="mb-8">
              <div className="flex items-center gap-2 mb-4 text-xl font-bold">
                <span role="img" aria-label={group.title}>
                  {group.icon}
                </span>
                <span>{group.title}</span>
              </div>
              <div className="flex flex-col">
                {group.faqIds.map((id) => {
                  const faq = faqs.find((f) => f.id === id);
                  if (!faq) return null;
                  return (
                    <div key={faq.id}>
                      <div
                        onClick={() => handleClickFaq(faq.id)}
                        className={`self-stretch px-6 py-4 cursor-pointer w-3/4 rounded-xl inline-flex justify-between items-center transition-all ${
                          faq.id === activeFaq
                            ? "bg-primary"
                            : "bg-accent hover:bg-primary-100 dark:bg-black"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span role="img" aria-hidden="true">
                            🔎
                          </span>
                          <span
                            className={`justify-start w-full text-[#525257] text-base font-medium leading-normal ${
                              faq.id === activeFaq ? "text-white" : "dark:text-gray-300"
                            }`}
                            dangerouslySetInnerHTML={{ __html: faq.question }}
                          />
                        </div>
                        <Image
                          src="/next/next_assets/images/svg/arrow-right.svg"
                          alt=""
                          width={20}
                          height={20}
                          className={`transform transition-transform duration-200 ${
                            faq.id === activeFaq ? "rotate-90" : ""
                          }`}
                        />
                      </div>
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          faq.id === activeFaq ? "max-h-[9999px] opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="px-6 py-3 text-sm text-black dark:text-gray-300 font-['Poppins'] leading-tight space-y-2">
                          {faq.answer.split("\n").map((line, index) => (
                            <p
                              key={index}
                              className="mb-2"
                              dangerouslySetInnerHTML={{ __html: line }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex justify-center items-center gap-6 flex-col">
          <Link href={"https://www.proz.com/faq"}>
            <Button variant="default" size="lg">
              View all FAQs
            </Button>
          </Link>
          <div className="flex flex-col md:flex-row items-center gap-3">
            <Link href={"https://www.proz.com/guidance"}>
              <Button variant="secondary" size="lg">
                Guidance center
              </Button>
            </Link>
            <Link href={"https://www.proz.com/about.php/security"}>
              <Button variant="secondary" size="lg">
                Security
              </Button>
            </Link>
            <Link href={"https://www.proz.com/forum/prozcom_technical_support-10.html"}>
              <Button variant="secondary" size="lg">
                Support forum
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportCenterPage;
